
import { create } from 'zustand';
import { api } from '@/lib/api';
import { showToast } from '@/lib/utils/toast';
import { useAuthStore } from '@/lib/stores/auth';
import type {
  CommentsStore,
  Comment,
  CommentFilters,
  AutoModerationRules,
  CommentsResponse,
  ModerationStats
} from './types';

const autoModerationRuleCache = new Map<number, AutoModerationRules>();
let tempCommentSequence = 0;

// Generation counter: incremented each time a new fetchStreamComments is started,
// or when createComment completes (to invalidate any in-flight stale fetches).
let fetchStreamCommentsGeneration = 0;

// API functions
const apiFetchComments = async (
  filters?: Partial<CommentFilters>,
  page = 1,
  pageSize = 20
): Promise<CommentsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
  });

  if (filters?.streamId) params.set('streamId', filters.streamId.toString());

  const response = await api.get<CommentsResponse>(`comments?${params}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch comments');
  }
  if (!response.data) {
    throw new Error('Invalid response: no data');
  }
  return response.data;
};

const apiCreateComment = async (
  streamId: number,
  content: string,
  parentId?: number
): Promise<Comment> => {
  const response = await api.post<{ comment: Comment }>('comments', {
    streamId,
    content,
    parentId,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to create comment');
  }
  if (!response.data) {
    throw new Error('Invalid response: no data');
  }
  return response.data.comment;
};

const apiSetCommentReaction = async (
  commentId: number,
  reaction: 'like' | 'dislike' | null
): Promise<void> => {
  const response = await api.post(`api/comments/${commentId}/reaction`, { reaction });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to set comment reaction');
  }
};

const apiModerateComment = async (
  commentId: number,
  approved: boolean,
  reason?: string
): Promise<void> => {
  const response = await api.put(`api/comments/${commentId}/moderate`, {
    action: approved ? 'approve' : 'reject',
    reason
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to moderate comment');
  }
};

const apiDeleteComment = async (commentId: number): Promise<void> => {
  const response = await api.delete(`api/comments/${commentId}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete comment');
  }
};

const apiUpdateComment = async (commentId: number, content: string): Promise<void> => {
  const response = await api.put(`api/comments/${commentId}`, { content });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to update comment');
  }
};

const apiReportComment = async (commentId: number, reason: string): Promise<void> => {
  const response = await api.post(`api/comments/${commentId}/report`, { reason });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to report comment');
  }
};

const apiTranslateComment = async (
  commentId: number,
  targetLang: string
): Promise<{ translatedText: string; cached: boolean }> => {
  const response = await api.post<{ translatedText: string; cached: boolean }>(
    `api/comments/${commentId}/translate`,
    { targetLang }
  );
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to translate comment');
  }
  if (!response.data) {
    throw new Error('Invalid response: no data');
  }
  return response.data;
};

const createTempComment = (
  streamId: number,
  content: string,
  parentId?: number
): Comment => {
  const tempId = `temp-${Date.now()}-${tempCommentSequence++}`;
  return {
    id: -Date.now() - tempCommentSequence,
    tempId,
    streamId,
    userId: 0,
    userName: 'You',
    content,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    moderated: false,
    likes: 0,
    userLiked: false,
    replyCount: 0,
    parentId,
    isPending: true,
  } as Comment;
};

const mapCommentTree = (comments: Comment[], updater: (comment: Comment) => Comment): Comment[] =>
  comments.map((comment) => {
    const nextComment = updater(comment);
    return nextComment.replies?.length
      ? { ...nextComment, replies: mapCommentTree(nextComment.replies, updater) }
      : nextComment;
  });

const insertCommentIntoTree = (
  comments: Comment[],
  parentId: number | undefined,
  comment: Comment
): Comment[] => {
  if (!parentId) {
    return [comment, ...comments];
  }

  return comments.map((current) => {
    if (current.id === parentId) {
      return {
        ...current,
        replies: [...(current.replies || []), comment],
        replyCount: (current.replyCount || 0) + 1,
      };
    }

    if (current.replies?.length) {
      return {
        ...current,
        replies: insertCommentIntoTree(current.replies, parentId, comment),
      };
    }

    return current;
  });
};

const replaceCommentInTree = (
  comments: Comment[],
  tempKey: number | string,
  nextComment: Comment
): Comment[] => comments.map((comment) => {
  if (comment.id === tempKey || comment.tempId === tempKey) {
    return {
      ...nextComment,
      isPending: false,
      tempId: undefined,
    };
  }

  if (comment.replies?.length) {
    return {
      ...comment,
      replies: replaceCommentInTree(comment.replies, tempKey, nextComment),
    };
  }

  return comment;
});

const removeCommentFromTree = (
  comments: Comment[],
  tempKey: number | string
): Comment[] => comments
  .filter((comment) => comment.id !== tempKey && comment.tempId !== tempKey)
  .map((comment) => {
    if (!comment.replies?.length) return comment;
    const nextReplies = removeCommentFromTree(comment.replies, tempKey);
    return {
      ...comment,
      replies: nextReplies,
      replyCount: Math.max(0, (comment.replyCount || 0) - (comment.replies.length - nextReplies.length)),
    };
  });

const removeCommentsFromTree = (
  comments: Comment[],
  commentIds: number[]
): Comment[] => comments
  .filter((comment) => !commentIds.includes(comment.id))
  .map((comment) => {
    if (!comment.replies?.length) return comment;
    const nextReplies = removeCommentsFromTree(comment.replies, commentIds);
    return {
      ...comment,
      replies: nextReplies,
      replyCount: Math.max(
        0,
        (comment.replyCount || 0) - (comment.replies.length - nextReplies.length)
      ),
    };
  });

const upsertCommentIntoTree = (comments: Comment[], comment: Comment): Comment[] => {
  const existingIndex = comments.findIndex((current) => current.id === comment.id);
  if (existingIndex >= 0) {
    const next = [...comments];
    next[existingIndex] = {
      ...comment,
      replies: comment.replies ?? next[existingIndex].replies,
    };
    return next;
  }

  if (!comment.parentId) {
    return [comment, ...comments];
  }

  return comments.map((current) => {
    if (current.id === comment.parentId) {
      const replyExists = (current.replies || []).some((reply) => reply.id === comment.id);
      return {
        ...current,
        replies: replyExists
          ? (current.replies || []).map((reply) => (reply.id === comment.id ? comment : reply))
          : [...(current.replies || []), comment],
        replyCount: Math.max(0, (current.replyCount || 0) + (replyExists ? 0 : 1)),
      };
    }

    if (current.replies?.length) {
      return {
        ...current,
        replies: upsertCommentIntoTree(current.replies, comment),
      };
    }

    return current;
  });
};

const updateCommentById = (
  comments: Comment[],
  commentId: number,
  updater: (comment: Comment) => Comment
): Comment[] => comments.map((comment) => {
  if (comment.id === commentId) {
    return updater(comment);
  }

  if (comment.replies?.length) {
    return {
      ...comment,
      replies: updateCommentById(comment.replies, commentId, updater),
    };
  }

  return comment;
});

const findCommentById = (
  comments: Comment[],
  commentId: number
): Comment | undefined => {
  for (const comment of comments) {
    if (comment.id === commentId) return comment;
    if (comment.replies?.length) {
      const nested = findCommentById(comment.replies, commentId);
      if (nested) return nested;
    }
  }
  return undefined;
};

const findMatchingTempComment = (
  comments: Comment[],
  incoming: Comment
): Comment | undefined => {
  for (const comment of comments) {
    const sameStream = comment.streamId === incoming.streamId;
    const sameParent = comment.parentId === incoming.parentId;
    const sameContent = comment.content.trim() === incoming.content.trim();
    if (comment.isPending && sameStream && sameParent && sameContent) {
      return comment;
    }
    if (comment.replies?.length) {
      const nested = findMatchingTempComment(comment.replies, incoming);
      if (nested) return nested;
    }
  }
  return undefined;
};

const patchRealtimeComment = (comment: Comment, incoming: Partial<Comment>): Comment => ({
  ...comment,
  content: incoming.content ?? comment.content,
  updatedAt: incoming.updatedAt ?? comment.updatedAt,
  moderated: incoming.moderated ?? comment.moderated,
  moderationAction: incoming.moderationAction ?? comment.moderationAction,
  moderationReason: incoming.moderationReason ?? comment.moderationReason,
  moderatedAt: incoming.moderatedAt ?? comment.moderatedAt,
  moderatedBy: incoming.moderatedBy ?? comment.moderatedBy,
  pinned: incoming.pinned ?? comment.pinned,
  highlighted: incoming.highlighted ?? comment.highlighted,
  replyCount: incoming.replyCount ?? comment.replyCount,
  likes: incoming.likes ?? comment.likes,
  userLiked: incoming.userLiked ?? comment.userLiked,
  flaggedWords: incoming.flaggedWords ?? comment.flaggedWords,
  spamScore: incoming.spamScore ?? comment.spamScore,
  sentimentScore: incoming.sentimentScore ?? comment.sentimentScore,
  replies: comment.replies,
});

export const useCommentsStore = create<CommentsStore>((set, get) => ({
  // Initial state
  comments: [],
  currentStreamComments: [],
  expandedReplies: new Set(),
  isLoading: false,
  hasFetchedInitially: false,
  error: null,
  filters: {
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    hasMore: false
  },
  isConnected: false,
  lastUpdate: null,
  pendingReactionIds: new Set(),
  pendingModerationIds: new Set(),
  pendingDeleteIds: new Set(),
  pendingReportIds: new Set(),

  // Actions
  fetchComments: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchComments(currentFilters);

      set({
        comments: response.comments,
        pagination: response.pagination,
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comments';
      set({
        error: errorMessage,
        isLoading: false,
        comments: []
      });
      throw error;
    }
  },

  fetchCommentReplies: async (commentId, page = 1) => {
    try {
      const state = get();
      const allComments = state.currentStreamComments.length > 0 ? state.currentStreamComments : state.comments;
      const replies = allComments.filter((comment) => comment.parentId === commentId);

      set((currentState) => ({
        comments: updateCommentById(currentState.comments, commentId, (comment) => ({
          ...comment,
          replies,
          replyCount: replies.length
        })),
        currentStreamComments: updateCommentById(currentState.currentStreamComments, commentId, (comment) => ({
          ...comment,
          replies,
          replyCount: replies.length
        }))
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comment replies';
      set({ error: errorMessage });
      throw error;
    }
  },

  fetchStreamComments: async (streamId) => {
    const myGeneration = ++fetchStreamCommentsGeneration;

    try {
      const { hasFetchedInitially } = get();
      // Only show loading state on initial fetch, not during polling refreshes
      // This prevents the skeleton loader from flashing on every poll
      if (!hasFetchedInitially) {
        set({ isLoading: true, error: null });
      }

      const response = await apiFetchComments({ streamId });

      // Skip stale responses — a newer fetch or createComment invalidated this one
      if (myGeneration !== fetchStreamCommentsGeneration) return;

      set({
        currentStreamComments: response.comments,
        isLoading: false,
        hasFetchedInitially: true,
        error: null
      });

    } catch (error) {
      if (myGeneration !== fetchStreamCommentsGeneration) return;

      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch stream comments';
      set({
        error: errorMessage,
        isLoading: false,
        hasFetchedInitially: true, // Mark as fetched even on error to prevent repeated loading states
        // Do NOT clear currentStreamComments — preserve any optimistic updates
      });
    }
  },

  createComment: async (streamId, content, parentId) => {
    let tempComment: Comment | null = null;
    try {
      set({ error: null });

      tempComment = createTempComment(streamId, content, parentId);
      const activeTempComment = tempComment;
      if (!activeTempComment) {
        throw new Error('Failed to create optimistic comment');
      }

      fetchStreamCommentsGeneration++;
      set((state) => ({
        comments: insertCommentIntoTree(state.comments, parentId, activeTempComment),
        currentStreamComments: insertCommentIntoTree(state.currentStreamComments, parentId, activeTempComment),
      }));

      const newComment = await apiCreateComment(streamId, content, parentId);

      set((state) => ({
        comments: replaceCommentInTree(state.comments, activeTempComment.tempId ?? activeTempComment.id, newComment),
        currentStreamComments: replaceCommentInTree(state.currentStreamComments, activeTempComment.tempId ?? activeTempComment.id, newComment),
      }));

      return newComment;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create comment';
      set((state) => ({
        error: errorMessage,
        comments: tempComment ? removeCommentFromTree(state.comments, tempComment.tempId ?? tempComment.id) : state.comments,
        currentStreamComments: tempComment ? removeCommentFromTree(state.currentStreamComments, tempComment.tempId ?? tempComment.id) : state.currentStreamComments,
      }));
      showToast.error('Failed to post comment', errorMessage);
      throw error;
    }
  },

  updateComment: async (commentId, content) => {
    try {
      set({ isLoading: true, error: null });
      await apiUpdateComment(commentId, content);

      set((state) => ({
        comments: updateCommentById(state.comments, commentId, (comment) => ({
          ...comment,
          content,
          updatedAt: new Date().toISOString()
        })),
        currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
          ...comment,
          content,
          updatedAt: new Date().toISOString()
        })),
        isLoading: false
      }));

      showToast.success('Comment updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update comment';
      set({
        error: errorMessage,
        isLoading: false
      });
      showToast.error('Failed to update comment');
      throw error;
    }
  },

  deleteComment: async (commentId) => {
    try {
      set((state) => ({
        error: null,
        pendingDeleteIds: new Set([...state.pendingDeleteIds, commentId]),
      }));

      await apiDeleteComment(commentId);

      // Remove comment from all locations
      set((state) => ({
        comments: removeCommentFromTree(state.comments, commentId),
        currentStreamComments: removeCommentFromTree(state.currentStreamComments, commentId),
        pendingDeleteIds: new Set([...state.pendingDeleteIds].filter((id) => id !== commentId)),
      }));

      showToast.success('Comment deleted');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete comment';
      set({
        error: errorMessage,
        pendingDeleteIds: new Set([...get().pendingDeleteIds].filter((id) => id !== commentId))
      });
      showToast.error('Failed to delete comment');
      throw error;
    }
  },

  likeComment: async (commentId) => {
    const currentTop = findCommentById(get().comments, commentId);
    const currentStream = findCommentById(get().currentStreamComments, commentId);
    const previous = currentTop || currentStream;
    if (!previous) return;
    if (get().pendingReactionIds.has(commentId)) return;

    const applyToggle = (comment: Comment): Comment => ({
      ...comment,
      likes: comment.userLiked ? Math.max(0, comment.likes - 1) : comment.likes + 1,
      userLiked: !comment.userLiked
    });

    try {
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds, commentId]),
        comments: mapCommentTree(state.comments, (comment) =>
          comment.id === commentId ? applyToggle(comment) : comment
        ),
        currentStreamComments: mapCommentTree(state.currentStreamComments, (comment) =>
          comment.id === commentId ? applyToggle(comment) : comment
        )
      }));

      await apiSetCommentReaction(commentId, 'like');

    } catch (error) {
      console.error('Failed to like comment:', error);
      showToast.error('Failed to like comment');
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds].filter((id) => id !== commentId)),
        comments: previous ? updateCommentById(state.comments, commentId, () => previous) : state.comments,
        currentStreamComments: previous ? updateCommentById(state.currentStreamComments, commentId, () => previous) : state.currentStreamComments
      }));
      throw error;
    } finally {
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds].filter((id) => id !== commentId)),
      }));
    }
  },

  unlikeComment: async (commentId) => {
    const currentTop = findCommentById(get().comments, commentId);
    const currentStream = findCommentById(get().currentStreamComments, commentId);
    const previous = currentTop || currentStream;
    if (!previous) return;
    if (get().pendingReactionIds.has(commentId)) return;

    const applyUnlike = (comment: Comment): Comment => ({
      ...comment,
      likes: Math.max(0, comment.likes - (comment.userLiked ? 1 : 0)),
      userLiked: false
    });

    try {
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds, commentId]),
        comments: mapCommentTree(state.comments, (comment) =>
          comment.id === commentId ? applyUnlike(comment) : comment
        ),
        currentStreamComments: mapCommentTree(state.currentStreamComments, (comment) =>
          comment.id === commentId ? applyUnlike(comment) : comment
        )
      }));

      await apiSetCommentReaction(commentId, null);
    } catch (error) {
      console.error('Failed to unlike comment:', error);
      showToast.error('Failed to unlike comment');
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds].filter((id) => id !== commentId)),
        comments: previous ? updateCommentById(state.comments, commentId, () => previous) : state.comments,
        currentStreamComments: previous ? updateCommentById(state.currentStreamComments, commentId, () => previous) : state.currentStreamComments
      }));
      throw error;
    } finally {
      set((state) => ({
        pendingReactionIds: new Set([...state.pendingReactionIds].filter((id) => id !== commentId)),
      }));
    }
  },

  reportComment: async (commentId, reason) => {
    try {
      set((state) => ({
        error: null,
        pendingReportIds: new Set([...state.pendingReportIds, commentId]),
      }));

      await apiReportComment(commentId, reason);

      set((state) => ({
        comments: updateCommentById(state.comments, commentId, (comment) => ({
          ...comment,
          moderated: true,
          moderationAction: 'flagged',
          moderationReason: reason,
          updatedAt: new Date().toISOString()
        })),
        currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
          ...comment,
          moderated: true,
          moderationAction: 'flagged',
          moderationReason: reason,
          updatedAt: new Date().toISOString()
        })),
        pendingReportIds: new Set([...state.pendingReportIds].filter((id) => id !== commentId))
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to report comment';
      set({
        error: errorMessage,
        pendingReportIds: new Set([...get().pendingReportIds].filter((id) => id !== commentId))
      });
      throw error;
    }
  },

  moderateComment: async (commentId, approved, reason) => {
    try {
      set((state) => ({
        error: null,
        pendingModerationIds: new Set([...state.pendingModerationIds, commentId]),
      }));

      await apiModerateComment(commentId, approved, reason);

      // Update comment moderation status
      set((state) => ({
        comments: updateCommentById(state.comments, commentId, (comment) => ({
          ...comment,
          moderated: !approved,
          moderationReason: approved ? undefined : reason,
          updatedAt: new Date().toISOString()
        })),
        currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
          ...comment,
          moderated: !approved,
          moderationReason: approved ? undefined : reason,
          updatedAt: new Date().toISOString()
        })),
        pendingModerationIds: new Set([...state.pendingModerationIds].filter((id) => id !== commentId))
      }));

      showToast.success(approved ? 'Comment approved' : 'Comment rejected');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to moderate comment';
      set({
        error: errorMessage,
        pendingModerationIds: new Set([...get().pendingModerationIds].filter((id) => id !== commentId))
      });
      showToast.error('Failed to moderate comment');
      throw error;
    }
  },

  bulkModerateComments: async (commentIds, approved, reason) => {
    try {
      set({ isLoading: true, error: null });

      // Moderate each comment
      await Promise.all(
        commentIds.map(id => apiModerateComment(id, approved, reason))
      );

      // Update all comments at once
      set((state) => ({
        comments: mapCommentTree(state.comments, (comment) =>
          commentIds.includes(comment.id)
            ? {
              ...comment,
              moderated: !approved,
              moderationReason: approved ? undefined : reason,
              updatedAt: new Date().toISOString()
            }
            : comment
        ),
        currentStreamComments: mapCommentTree(state.currentStreamComments, (comment) =>
          commentIds.includes(comment.id)
            ? {
              ...comment,
              moderated: !approved,
              moderationReason: approved ? undefined : reason,
              updatedAt: new Date().toISOString()
            }
            : comment
        )
      }));

      set({ isLoading: false });
      showToast.success(`${commentIds.length} comments moderated`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to moderate comments';
      set({
        error: errorMessage,
        isLoading: false
      });
      showToast.error('Failed to moderate comments');
      throw error;
    }
  },

  // UI State Management
  toggleReplies: (commentId) => {
    set((state) => {
      const newExpanded = new Set(state.expandedReplies);
      if (newExpanded.has(commentId)) {
        newExpanded.delete(commentId);
      } else {
        newExpanded.add(commentId);
      }
      return { expandedReplies: newExpanded };
    });
  },

  expandAllReplies: (commentId) => {
    set((state) => ({
      expandedReplies: new Set([...state.expandedReplies, commentId])
    }));
  },

  collapseAllReplies: (commentId) => {
    set((state) => {
      const newExpanded = new Set(state.expandedReplies);
      newExpanded.delete(commentId);
      return { expandedReplies: newExpanded };
    });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 }
    }));
  },

  loadMoreComments: async () => {
    const state = get();
    if (state.isLoading || !state.pagination.hasMore) return;

    try {
      set({ isLoading: true });

      const nextPage = state.pagination.page + 1;
      const response = await apiFetchComments(state.filters, nextPage, state.pagination.pageSize);

      set((currentState) => ({
        comments: [...currentState.comments, ...response.comments],
        pagination: response.pagination,
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more comments';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  resetComments: () => {
    set({
      comments: [],
      currentStreamComments: [],
      expandedReplies: new Set(),
      hasFetchedInitially: false,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        hasMore: false
      }
    });
  },

  // Real-time functionality with Supabase Realtime
  connectToStream: async (streamId) => {
    try {
      console.log('Connecting to stream comments via Supabase Realtime:', streamId);

      const { supabase } = await import('@/lib/supabase/client');

      // Unsubscribe any existing comments channel first
      const existing = supabase.getChannels();
      for (const ch of existing) {
        if (ch.topic.startsWith('realtime:comments-')) {
          await supabase.removeChannel(ch);
        }
      }

      const channelName = `comments-stream-${streamId}`;
      supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'comments',
            filter: `stream_id=eq.${streamId}`,
          },
          (payload) => {
            const newComment = payload.new as any;
            get().handleRealtimeComment(newComment as Comment);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'comments',
            filter: `stream_id=eq.${streamId}`,
          },
          (payload) => {
            const updatedComment = payload.new as any;
            if (updatedComment?.id == null) {
              get().fetchStreamComments(streamId);
              return;
            }

            const nextComment = updatedComment as Comment;
            set((state) => ({
              comments: updateCommentById(state.comments, nextComment.id, (comment) =>
                patchRealtimeComment(comment, nextComment)
              ),
              currentStreamComments: updateCommentById(state.currentStreamComments, nextComment.id, (comment) =>
                patchRealtimeComment(comment, nextComment)
              ),
              lastUpdate: new Date().toISOString(),
            }));
          }
        )
        .subscribe((status) => {
          console.log(`[Realtime] comments channel status: ${status}`);
          set({ isConnected: status === 'SUBSCRIBED', lastUpdate: new Date().toISOString() });
        });

      showToast.success('Connected to live chat');

    } catch (error) {
      console.error('Failed to connect to stream:', error);
      showToast.error('Failed to connect to live chat');
      throw error;
    }
  },

  disconnectFromStream: async () => {
    console.log('Disconnecting from stream comments');
    try {
      const { supabase } = await import('@/lib/supabase/client');
      const channels = supabase.getChannels();
      for (const ch of channels) {
        if (ch.topic.startsWith('realtime:comments-')) {
          await supabase.removeChannel(ch);
        }
      }
    } catch { /* ignore */ }
    set({ isConnected: false });
    showToast.info('Disconnected from live chat');
  },

  handleRealtimeComment: (comment) => {
    set((state) => {
      const alreadyExists =
        findCommentById(state.comments, comment.id) ||
        findCommentById(state.currentStreamComments, comment.id);
      if (alreadyExists) {
        return { ...state, lastUpdate: new Date().toISOString() };
      }

      const matchingTemp = findMatchingTempComment(state.currentStreamComments, comment)
        || findMatchingTempComment(state.comments, comment);

      if (matchingTemp) {
        const tempKey = matchingTemp.tempId ?? matchingTemp.id;
        return {
          comments: replaceCommentInTree(state.comments, tempKey, comment),
          currentStreamComments: replaceCommentInTree(state.currentStreamComments, tempKey, comment),
          lastUpdate: new Date().toISOString()
        };
      }

      if (comment.parentId == null) {
        return {
          comments: [comment, ...state.comments],
          currentStreamComments: [comment, ...state.currentStreamComments],
          lastUpdate: new Date().toISOString()
        };
      }

      return {
        comments: insertCommentIntoTree(state.comments, comment.parentId ?? undefined, comment),
        currentStreamComments: insertCommentIntoTree(state.currentStreamComments, comment.parentId ?? undefined, comment),
        lastUpdate: new Date().toISOString()
      };
    });
  },

  handleRealtimeLike: (commentId, userId, liked) => {
    // In a real app, userId would be compared against the logged-in user's ID
    const currentUserId = useAuthStore.getState().user?.id;
    const isCurrentUser = currentUserId != null && String(currentUserId) === String(userId);

    set((state) => ({
      comments: updateCommentById(state.comments, commentId, (comment) => ({
        ...comment,
        likes: liked ? comment.likes + 1 : Math.max(0, comment.likes - 1),
        userLiked: isCurrentUser ? liked : comment.userLiked,
      })),
      currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
        ...comment,
        likes: liked ? comment.likes + 1 : Math.max(0, comment.likes - 1),
        userLiked: isCurrentUser ? liked : comment.userLiked,
      })),
      lastUpdate: new Date().toISOString()
    }));
  },

  // Utility functions
  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  },

  // Advanced moderation methods
  setAutoModerationRules: async (streamId, rules) => {
    autoModerationRuleCache.set(streamId, rules);
    showToast.success('Auto-moderation rules updated');
  },

  getModerationStats: async (streamId) => {
    const state = get();
    const baseComments = state.comments.filter((comment) => comment.streamId === streamId);
    const moderatedComments = baseComments.filter((comment) => comment.moderated);
    const rejectedComments = moderatedComments.filter((comment) =>
      (comment.moderationReason || '').toLowerCase().includes('reject') ||
      comment.moderationAction === 'rejected'
    );
    const flaggedComments = moderatedComments.filter((comment) =>
      (comment.moderationReason || '').toLowerCase().includes('flag') ||
      comment.moderationAction === 'flagged'
    );
    const spamComments = moderatedComments.filter((comment) =>
      (comment.moderationReason || '').toLowerCase().includes('spam')
    );
    const approvedComments = Math.max(0, baseComments.length - moderatedComments.length);

    const wordCounts = new Map<string, number>();
    for (const comment of flaggedComments) {
      for (const word of comment.flaggedWords || []) {
        wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
      }
    }

    const topFlaggedWords = [...wordCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([word, count]) => ({ word, count }));

    return {
      totalComments: baseComments.length,
      pendingModeration: moderatedComments.length,
      approvedComments,
      rejectedComments: rejectedComments.length,
      flaggedComments: flaggedComments.length,
      spamComments: spamComments.length,
      moderationQueue: moderatedComments.length,
      averageResponseTime: 0,
      topFlaggedWords,
      moderationActivity: []
    } satisfies ModerationStats;
  },

  searchComments: async (streamId, query, filters) => {
    const state = get();
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    const source = state.comments.filter((comment) => comment.streamId === streamId);
    return source.filter((comment) => {
      const contentMatch = comment.content.toLowerCase().includes(normalizedQuery);
      if (!contentMatch) {
        return false;
      }

      if (filters?.moderated !== undefined && comment.moderated !== filters.moderated) {
        return false;
      }

      if (filters?.flaggedOnly) {
        const reason = (comment.moderationReason || '').toLowerCase();
        if (!reason.includes('flag') && comment.moderationAction !== 'flagged') {
          return false;
        }
      }

      return true;
    });
  },

  pinComment: async (commentId, pinned) => {
      set((state) => ({
        comments: updateCommentById(state.comments, commentId, (comment) => ({
          ...comment,
          pinned,
          updatedAt: new Date().toISOString()
        })),
        currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
          ...comment,
          pinned,
          updatedAt: new Date().toISOString()
        }))
      }));
  },

  highlightComment: async (commentId, highlighted) => {
      set((state) => ({
        comments: updateCommentById(state.comments, commentId, (comment) => ({
          ...comment,
          highlighted,
          updatedAt: new Date().toISOString()
        })),
        currentStreamComments: updateCommentById(state.currentStreamComments, commentId, (comment) => ({
          ...comment,
          highlighted,
          updatedAt: new Date().toISOString()
        }))
      }));
  },

  flagComment: async (commentId, reason) => {
    await get().reportComment(commentId, reason);
  },

  bulkFlagComments: async (commentIds, reason) => {
    try {
      set({ isLoading: true, error: null });

      await Promise.all(commentIds.map((commentId) => apiReportComment(commentId, reason)));

      set((state) => ({
        comments: mapCommentTree(state.comments, (comment) =>
          commentIds.includes(comment.id)
            ? {
              ...comment,
              moderated: true,
              moderationAction: 'flagged',
              moderationReason: reason,
              updatedAt: new Date().toISOString()
            }
            : comment
        ),
        currentStreamComments: mapCommentTree(state.currentStreamComments, (comment) =>
          commentIds.includes(comment.id)
            ? {
              ...comment,
              moderated: true,
              moderationAction: 'flagged',
              moderationReason: reason,
              updatedAt: new Date().toISOString()
            }
            : comment
        ),
      }));

      set({ isLoading: false });
      showToast.success(`${commentIds.length} comments flagged`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to flag comments';
      set({
        error: errorMessage,
        isLoading: false
      });
      showToast.error('Failed to flag comments');
      throw error;
    }
  },

  bulkDeleteComments: async (commentIds) => {
    try {
      set({ isLoading: true, error: null });

      // Delete each comment
      await Promise.all(
        commentIds.map(id => apiDeleteComment(id))
      );

      // Remove all deleted comments from state
      set((state) => ({
        comments: removeCommentsFromTree(state.comments, commentIds),
        currentStreamComments: removeCommentsFromTree(state.currentStreamComments, commentIds)
      }));

      set({ isLoading: false });
      showToast.success(`${commentIds.length} comments deleted`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete comments';
      set({
        error: errorMessage,
        isLoading: false
      });
      showToast.error('Failed to delete comments');
      throw error;
    }
  },

  translateComment: async (commentId: number, targetLang: string) => {
    try {
      const result = await apiTranslateComment(commentId, targetLang);
      showToast.success(result.cached ? 'Translation loaded from cache' : 'Comment translated');
      return result.translatedText;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to translate comment';
      showToast.error('Failed to translate comment');
      throw error;
    }
  }
}));

// Selectors for common comments state
export const useComments = () => useCommentsStore((state) => state.comments);
export const useCurrentStreamComments = () => useCommentsStore((state) => state.currentStreamComments);
export const useCommentsLoading = () => useCommentsStore((state) => state.isLoading);
export const useCommentsError = () => useCommentsStore((state) => state.error);
export const useExpandedReplies = () => useCommentsStore((state) => state.expandedReplies);
export const useCommentsFilters = () => useCommentsStore((state) => state.filters);

// Helper hooks for comment interactions
export const useCanModerateComments = () => {
  const { user } = useAuthStore();
  // Moderation allowed for pastor (2), media_team (3), and admin (4)
  return user?.role?.id && user.role.id >= 2;
};

export const useCanEditComment = (commentUserId: number) => {
  const { user } = useAuthStore();
  // Users can edit their own comments, or moderators can edit any comment
  const isOwnComment = user?.id === commentUserId;
  const canModerate = user?.role?.id && user.role.id >= 2;
  return isOwnComment || canModerate;
};

export const useCommentLikeStatus = (commentId: number) => {
  const comments = useComments();
  const streamComments = useCurrentStreamComments();

  const comment = findCommentById(comments, commentId) ||
    findCommentById(streamComments, commentId);

  return {
    likes: comment?.likes || 0,
    userLiked: comment?.userLiked || false
  };
};

// Helper hook for real-time connection status
export const useCommentsConnection = () => {
  const isConnected = useCommentsStore((state) => state.isConnected);
  const lastUpdate = useCommentsStore((state) => state.lastUpdate);

  return {
    isConnected,
    lastUpdate,
    connectionStatus: isConnected ? 'connected' : 'disconnected'
  };
};
